import React, { useMemo, useState } from "react";
import { Search, Bandage, Users, X, Archive, Clock, Pause } from "lucide-react";
import { GROUP_COLORS } from "../lib/api";

/**
 * SailorSidebar — squad roster picker.
 *
 * Three view modes (roster tabs):
 *   • Active   — real active roster (default)
 *   • Paused   — temporarily archived, expected back (shows return date
 *                chip, overdue ones flagged red). Only rendered when at
 *                least one athlete is paused.
 *   • Archived — permanently gone (moved on, joined Navy, etc.)
 *
 * Plus:
 *   • Inactivity badge   — "45d" chip on any ACTIVE athlete not touched
 *                          in 60+ days (paused/archived hide it — the
 *                          Paused/Archived chip is the signal already)
 */
export default function SailorSidebar({ sailors, selectedId, onSelect, injuriesBySailor }) {
  const [q, setQ] = useState("");
  const [squadFilter, setSquadFilter] = useState(null);
  // Roster mode — "active" | "paused" | "archived". Search query is
  // preserved across tab switches; squad filter is cleared because
  // each tab has its own set of squads with members.
  const [rosterMode, setRosterMode] = useState("active");

  // Split three ways so every tab count is accurate independent of
  // filters. Paused = archived + archive_type='paused', permanent =
  // everything else (including legacy rows without archive_type).
  const { activeList, pausedList, archivedList } = useMemo(() => {
    const active = [];
    const paused = [];
    const archived = [];
    for (const s of sailors || []) {
      if (!s.archived) { active.push(s); continue; }
      if ((s.archive_type || "permanent") === "paused") paused.push(s);
      else archived.push(s);
    }
    return { activeList: active, pausedList: paused, archivedList: archived };
  }, [sailors]);

  const source =
    rosterMode === "paused"   ? pausedList   :
    rosterMode === "archived" ? archivedList :
                                activeList;

  const squadCounts = useMemo(() => {
    const counts = new Map();
    for (const s of source) {
      const g = (s.group || "").trim();
      if (!g) continue;
      counts.set(g, (counts.get(g) || 0) + 1);
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [source]);

  const filtered = useMemo(() => {
    let list = source;
    if (squadFilter) list = list.filter((s) => (s.group || "") === squadFilter);
    if (q.trim()) {
      const lower = q.toLowerCase();
      list = list.filter((s) => s.name.toLowerCase().includes(lower));
    }
    return list;
  }, [source, q, squadFilter]);

  const todayISO = new Date().toISOString().slice(0, 10);

  return (
    <div className="card p-3 md:p-5 lg:sticky lg:top-4 h-full lg:h-auto lg:max-h-[calc(100vh-2rem)] flex flex-col" data-testid="sailor-sidebar">
      <div className="text-xs uppercase tracking-widest text-slate-400 mb-3">Athletes</div>

      {/* Roster mode tabs — Paused tab only renders when at least one
          athlete is paused so the sidebar stays quiet otherwise. */}
      <div
        className="grid gap-1 mb-3"
        style={{ gridTemplateColumns: pausedList.length > 0 ? "1fr 1fr 1fr" : "1fr 1fr" }}
        data-testid="sailor-roster-tabs"
      >
        <button
          onClick={() => { setRosterMode("active"); setSquadFilter(null); }}
          className={`text-[11px] px-2 py-1.5 rounded-lg border transition ${
            rosterMode === "active"
              ? "bg-cyan-500/15 border-cyan-500/45 text-cyan-200"
              : "bg-[#0E1A2E] border-[#1E304B] text-slate-400 hover:text-slate-200"
          }`}
          data-testid="sailor-tab-active"
        >
          Active <span className="opacity-70">· {activeList.length}</span>
        </button>
        {pausedList.length > 0 && (
          <button
            onClick={() => { setRosterMode("paused"); setSquadFilter(null); }}
            className={`text-[11px] px-2 py-1.5 rounded-lg border transition inline-flex items-center justify-center gap-1 ${
              rosterMode === "paused"
                ? "bg-sky-500/15 border-sky-500/45 text-sky-200"
                : "bg-[#0E1A2E] border-[#1E304B] text-slate-400 hover:text-slate-200"
            }`}
            data-testid="sailor-tab-paused"
            title="Athletes on temporary break — expected to return"
          >
            <Pause size={10} /> Paused <span className="opacity-70">· {pausedList.length}</span>
          </button>
        )}
        <button
          onClick={() => { setRosterMode("archived"); setSquadFilter(null); }}
          className={`text-[11px] px-2 py-1.5 rounded-lg border transition inline-flex items-center justify-center gap-1 ${
            rosterMode === "archived"
              ? "bg-amber-500/15 border-amber-500/45 text-amber-200"
              : "bg-[#0E1A2E] border-[#1E304B] text-slate-400 hover:text-slate-200"
          }`}
          data-testid="sailor-tab-archived"
          title="Athletes who have left the org"
        >
          <Archive size={10} /> Archived <span className="opacity-70">· {archivedList.length}</span>
        </button>
      </div>

      <div className="relative mb-3">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name..."
          className="w-full pl-9 pr-3 py-2 bg-[#0E1A2E] border border-[#1E304B] rounded-lg text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-[#00E5FF]/60 font-mono-data"
          data-testid="sailor-search-input"
        />
      </div>

      {squadCounts.length > 0 && (
        <div className="mb-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5 flex items-center gap-1.5">
            <Users size={10} /> Filter by squad
          </div>
          <div className="flex flex-wrap gap-1">
            {squadCounts.map(([name, n]) => {
              const active = squadFilter === name;
              const c = GROUP_COLORS[name] || "#7DD3FC";
              return (
                <button
                  key={name}
                  onClick={() => setSquadFilter(active ? null : name)}
                  className="text-[10px] px-1.5 py-0.5 rounded-full border font-mono-data transition"
                  style={active
                    ? { background: `${c}30`, borderColor: `${c}99`, color: c }
                    : { background: "#0E1A2E", borderColor: "#1E304B", color: "#CBD5E1" }}
                  title={`${n} athlete${n === 1 ? "" : "s"} · click to ${active ? "clear" : "filter"}`}
                  data-testid={`squad-filter-${name.replace(/\W+/g, "-")}`}
                >
                  <span style={{ color: c }}>●</span> {name} <span className="opacity-60">· {n}</span>
                </button>
              );
            })}
            {squadFilter && (
              <button
                onClick={() => setSquadFilter(null)}
                className="text-[10px] px-1.5 py-0.5 rounded-full border border-rose-500/40 bg-rose-500/10 text-rose-200 font-mono-data flex items-center gap-1"
                data-testid="squad-filter-clear"
              >
                <X size={9} /> clear
              </button>
            )}
          </div>
        </div>
      )}

      <div className="text-[11px] text-slate-500 font-mono-data mb-2">
        {filtered.length} of {source.length}
        {squadFilter && <span className="text-cyan-300"> · {squadFilter}</span>}
        {rosterMode === "paused"   && <span className="text-sky-300">   · paused</span>}
        {rosterMode === "archived" && <span className="text-amber-300"> · archived</span>}
      </div>

      <div className="overflow-y-auto flex-1 -mx-1 pr-1">
        {filtered.map((s) => {
          const active = s.id === selectedId;
          const isArch = Boolean(s.archived);
          const isPaused = isArch && (s.archive_type || "permanent") === "paused";
          const days = s.days_since_activity;
          const inactivityChip =
            !isArch && days != null && days >= 60
              ? {
                  label: days >= 365 ? `${Math.floor(days / 365)}y` : `${days}d`,
                  full: `Not attended for ${days} days`,
                  color: days >= 180 ? "#EF4444" : days >= 90 ? "#F59E0B" : "#FACC15",
                }
              : null;
          const returnOverdue = isPaused && s.expected_return_date && s.expected_return_date < todayISO;
          const dot =
            s.latest_issues >= 4 ? "#EF4444" :
            s.latest_issues >= 2 ? "#F59E0B" :
            s.latest_issues >= 1 ? "#FACC15" :
            "#10B981";
          return (
            <button
              key={s.id}
              onClick={() => onSelect(s.id)}
              className={`w-full text-left px-3 py-2.5 rounded-lg mb-1 transition-all border ${
                active
                  ? "bg-[#0E1A2E] border-[#00E5FF]/50 shadow-[0_4px_18px_rgba(0,229,255,0.12)]"
                  : "border-transparent hover:bg-[#0E1A2E] hover:border-[#1E304B]"
              } ${isArch ? "opacity-60" : ""}`}
              data-testid={`sailor-item-${s.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className={`font-display text-sm truncate flex items-center gap-1.5 ${
                  isArch ? "text-slate-400" : "text-slate-100"
                } ${isArch && !isPaused ? "line-through decoration-slate-600" : ""}`}>
                  {s.name}
                  {isPaused && (
                    <Pause size={10} className="text-sky-400 shrink-0" />
                  )}
                  {isArch && !isPaused && (
                    <Archive size={10} className="text-amber-400 shrink-0" />
                  )}
                  {(injuriesBySailor?.[s.id] || 0) > 0 && !isArch && (
                    <span
                      className="inline-flex items-center gap-0.5 px-1 py-0 rounded text-[9px] font-mono-data"
                      style={{ background: "#EF444422", color: "#FCA5A5", border: "1px solid #EF444455" }}
                      title={`${injuriesBySailor[s.id]} open injur${injuriesBySailor[s.id] === 1 ? "y" : "ies"}`}
                      data-testid={`sailor-injury-badge-${s.id}`}
                    >
                      <Bandage size={9} /> {injuriesBySailor[s.id]}
                    </span>
                  )}
                  {inactivityChip && (
                    <span
                      className="inline-flex items-center gap-0.5 px-1 py-0 rounded text-[9px] font-mono-data"
                      style={{
                        background: `${inactivityChip.color}22`,
                        color: inactivityChip.color,
                        border: `1px solid ${inactivityChip.color}55`,
                      }}
                      title={inactivityChip.full}
                      data-testid={`sailor-inactivity-badge-${s.id}`}
                    >
                      <Clock size={9} /> {inactivityChip.label}
                    </span>
                  )}
                  {returnOverdue && (
                    <span
                      className="inline-flex items-center gap-0.5 px-1 py-0 rounded text-[9px] font-mono-data"
                      style={{ background: "#EF444422", color: "#FCA5A5", border: "1px solid #EF444455" }}
                      title={`Overdue — expected back ${s.expected_return_date}`}
                      data-testid={`sailor-return-overdue-badge-${s.id}`}
                    >
                      overdue
                    </span>
                  )}
                </div>
                {!isArch && (
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: dot }} title={`${s.latest_issues} flags`} />
                )}
              </div>
              <div className="flex items-center justify-between mt-0.5 gap-2">
                <div className="text-[10px] text-slate-500 truncate flex items-center gap-1">
                  <span style={{ color: GROUP_COLORS[s.group] }}>●</span> {s.group}
                </div>
                <div className="flex items-center gap-1.5 text-[10px] text-slate-500 font-mono-data shrink-0">
                  {s.subgroup && (
                    <span
                      className="px-1.5 py-0.5 rounded bg-[#122036] border border-[#1E304B] text-cyan-200/80"
                      title="Class"
                    >
                      {s.subgroup}
                    </span>
                  )}
                  <span>{s.record_count} tests</span>
                </div>
              </div>
              {isPaused && s.expected_return_date && (
                <div className="mt-1 text-[10px] text-sky-300/80 truncate">
                  ↩ back {s.expected_return_date}
                  {s.archive_reason && <span className="text-slate-500"> · {s.archive_reason}</span>}
                </div>
              )}
              {isArch && !isPaused && (s.archive_reason || s.archive_destination) && (
                <div className="mt-1 text-[10px] text-amber-300/80 truncate" title={s.archive_reason || ""}>
                  {s.archive_destination ? `→ ${s.archive_destination}` : s.archive_reason}
                </div>
              )}
            </button>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-center text-slate-500 text-sm py-6">
            {rosterMode === "paused"
              ? "No paused athletes."
              : rosterMode === "archived"
                ? "No archived athletes yet."
                : "No athletes match"}
          </div>
        )}
      </div>
    </div>
  );
}
